/**
 * Timeline Utility Functions
 *
 * Pure functions for timeline calculations, snap detection, time formatting, etc.
 */

import type { TimelineClip } from './types'

/**
 * Format time in HH:MM:SS:FF format
 * @param seconds Time in seconds
 * @param fps Frames per second (default 30)
 */
export function formatTime(seconds: number, fps: number = 30): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const frames = Math.floor((seconds % 1) * fps)

  const hh = hours.toString().padStart(2, '0')
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  const ff = frames.toString().padStart(2, '0')

  if (hours > 0) {
    return `${hh}:${mm}:${ss}:${ff}`
  }
  return `${mm}:${ss}:${ff}`
}

/**
 * Format time in simple MM:SS format
 */
export function formatTimeSimple(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  return `${mm}:${ss}`
}

/**
 * Convert pixels to seconds based on zoom level
 */
export function pixelsToSeconds(pixels: number, zoomLevel: number): number {
  return pixels / zoomLevel
}

/**
 * Convert seconds to pixels based on zoom level
 */
export function secondsToPixels(seconds: number, zoomLevel: number): number {
  return seconds * zoomLevel
}

/**
 * Calculate clip position (in pixels) on timeline
 */
export function getClipPosition(clip: TimelineClip, clips: TimelineClip[], zoomLevel: number): number {
  // Sum durations of all clips before this one
  const clipsBeforeThis = clips.filter((c) => c.order < clip.order)
  const timeBeforeClip = clipsBeforeThis.reduce((sum, c) => sum + c.effectiveDuration, 0)
  return secondsToPixels(timeBeforeClip, zoomLevel)
}

/**
 * Calculate clip width (in pixels) on timeline
 */
export function getClipWidth(clip: TimelineClip, zoomLevel: number): number {
  return secondsToPixels(clip.effectiveDuration, zoomLevel)
}

/**
 * Find clip at a given time position
 */
export function getClipAtTime(clips: TimelineClip[], time: number): TimelineClip | null {
  let currentTime = 0

  for (const clip of clips.sort((a, b) => a.order - b.order)) {
    const clipEndTime = currentTime + clip.effectiveDuration
    if (time >= currentTime && time < clipEndTime) {
      return clip
    }
    currentTime = clipEndTime
  }

  return null
}

/**
 * Get time offset within a clip (relative to clip's trimmed start)
 */
export function getTimeOffsetInClip(clip: TimelineClip, clips: TimelineClip[], globalTime: number): number {
  let currentTime = 0

  for (const c of clips.sort((a, b) => a.order - b.order)) {
    if (c.id === clip.id) {
      return Math.max(0, Math.min(globalTime - currentTime, clip.effectiveDuration))
    }
    currentTime += c.effectiveDuration
  }

  return 0
}

/**
 * Snap detection threshold (in pixels)
 */
const SNAP_THRESHOLD = 10

/**
 * Find snap position for a clip being dragged
 * Returns the snap position in pixels if within threshold, null otherwise
 */
export function findSnapPosition(
  _draggedClip: TimelineClip,
  otherClips: TimelineClip[],
  currentPosition: number,
  zoomLevel: number
): number | null {
  // Calculate positions of all other clips
  const snapTargets: number[] = [0] // Always include timeline start

  otherClips.forEach((clip) => {
    const clipStart = getClipPosition(clip, otherClips, zoomLevel)
    const clipEnd = clipStart + getClipWidth(clip, zoomLevel)
    snapTargets.push(clipStart, clipEnd)
  })

  // Find closest snap target
  let closestSnap: number | null = null
  let minDistance = SNAP_THRESHOLD

  snapTargets.forEach((target) => {
    const distance = Math.abs(currentPosition - target)
    if (distance < minDistance) {
      minDistance = distance
      closestSnap = target
    }
  })

  return closestSnap
}

/**
 * Calculate new clip order based on drag position
 */
export function calculateNewOrder(
  draggedClipId: string,
  newPosition: number,
  clips: TimelineClip[],
  zoomLevel: number
): number {
  const draggedClip = clips.find((c) => c.id === draggedClipId)
  if (!draggedClip) return 0

  const otherClips = clips.filter((c) => c.id !== draggedClipId)

  // Find which position this clip should be at based on pixel position
  let cumulativeTime = 0
  let newOrder = 0

  for (let i = 0; i < otherClips.length; i++) {
    const clipPosition = secondsToPixels(cumulativeTime, zoomLevel)
    if (newPosition < clipPosition) {
      return newOrder
    }
    cumulativeTime += otherClips[i].effectiveDuration
    newOrder++
  }

  return newOrder
}

/**
 * Generate timeline ruler tick marks
 * Returns array of { position: pixels, label: string, isMajor: boolean }
 */
export interface RulerTick {
  position: number
  label: string
  isMajor: boolean
}

export function generateRulerTicks(
  totalDuration: number,
  zoomLevel: number,
  viewportWidth: number
): RulerTick[] {
  const ticks: RulerTick[] = []

  // Determine tick interval based on zoom level
  let majorInterval: number
  let minorInterval: number

  if (zoomLevel >= 100) {
    // Zoomed in: major every 1s, minor every 0.2s
    majorInterval = 1
    minorInterval = 0.2
  } else if (zoomLevel >= 50) {
    // Medium: major every 3s, minor every 1s
    majorInterval = 3
    minorInterval = 1
  } else {
    // Zoomed out: major every 10s, minor every 2s
    majorInterval = 10
    minorInterval = 2
  }

  const maxTime = Math.max(totalDuration, pixelsToSeconds(viewportWidth, zoomLevel))

  // Generate minor ticks
  for (let t = 0; t <= maxTime; t += minorInterval) {
    const isMajor = t % majorInterval === 0
    ticks.push({
      position: secondsToPixels(t, zoomLevel),
      label: isMajor ? formatTimeSimple(t) : '',
      isMajor,
    })
  }

  return ticks
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Calculate clip time boundaries (start and end in global timeline)
 */
export function getClipTimeBoundaries(
  clip: TimelineClip,
  clips: TimelineClip[]
): { startTime: number; endTime: number } {
  let startTime = 0

  for (const c of clips.sort((a, b) => a.order - b.order)) {
    if (c.id === clip.id) {
      return {
        startTime,
        endTime: startTime + clip.effectiveDuration,
      }
    }
    startTime += c.effectiveDuration
  }

  return { startTime: 0, endTime: 0 }
}
